/**
 * Ticket WK5: reine Logik der öffentlichen Wahlkalender-Seite je Bundesland.
 *
 * Ohne React, ohne Datenbank: Adressform des Bundeslandes, Gruppierung der
 * Gemeindenamen nach Anfangsbuchstaben und die Kopfangaben für Suchmaschinen.
 *
 * Auf dieser Seite erscheinen ausschließlich Gemeindenamen. Termine und Namen
 * amtierender Personen bleiben hinter der Anmeldung; dafür sorgt schon die
 * Leseschicht, die diese Felder gar nicht abfragt.
 */

import { BUNDESLAND_LABELS, type Bundesland } from "@/lib/contacts";
import { shareImageMeta, type ShareMetaEntry } from "@/lib/share-image-meta";

export { BUNDESLAND_LABELS };
export type { Bundesland };

export const WAHLKALENDER_PUBLIC_PREFIX = "/wahlkalender";

/**
 * Ticket WK7: Kürzel des Freebies, das die Anmeldung auf der Landesseite
 * freischaltet – dasselbe wie auf der eigenen Landingpage unter
 * `/kostenlos/wahlkalender` (Ticket WK6).
 */
export const WAHLKALENDER_MAGNET_SLUG = "wahlkalender";

/** Bundesland -> Adressform (`baden_wuerttemberg` -> `baden-wuerttemberg`). */
export function bundeslandSlug(bundesland: Bundesland): string {
  return bundesland.replace(/_/gu, "-");
}

/** Adressform -> Bundesland. Unbekannte Form = null (die Seite ist dann 404). */
export function bundeslandFromSlug(slug: string): Bundesland | null {
  const wanted = slug.trim().toLowerCase().replace(/-/gu, "_");
  const keys = Object.keys(BUNDESLAND_LABELS) as Bundesland[];
  return keys.find((key) => key === wanted) ?? null;
}

export function landesseitePath(bundesland: Bundesland): string {
  return `${WAHLKALENDER_PUBLIC_PREFIX}/${bundeslandSlug(bundesland)}`;
}

/* ------------------------------------------------------- Verzeichnis */

export type LetterGroup = { letter: string; names: string[] };

/** Eine Gemeinde, wie das Verzeichnis sie für die Beschriftung braucht. */
export type DirectoryMunicipality = { name: string; office?: string | null; district?: string | null };

/**
 * Ticket WK23: Beschriftung mit Wahlart. Maßgeblich ist allein das Amt
 * (`office`), nicht die Amtsbezeichnung – so bleibt sie geschlechtsneutral.
 * Doppelte Ortsnamen im Bundesland tragen den Landkreis als Zusatz.
 */
export function directoryLabel(entry: DirectoryMunicipality, duplicate: boolean): string {
  const name = municipalityDisplayName(entry.name);
  const wahl = entry.office === "buergermeister" ? "Bürgermeisterwahl" : "Oberbürgermeisterwahl";
  const district = entry.district?.trim();
  return duplicate && district ? `${wahl} ${name} (${district})` : `${wahl} ${name}`;
}

/**
 * Gemeinden nach Anfangsbuchstaben gruppieren. Umlaute werden ihrem
 * Grundbuchstaben zugeordnet (Ä zu A), sortiert wird nach deutscher Regel.
 * Ein Name, der nicht mit einem Buchstaben beginnt, landet in der Gruppe „#".
 * Buchstabe und Reihenfolge folgen dem Ortsnamen; mit Amtsangabe wird die
 * Beschriftung „<Wahlart> <Ort>" angezeigt (Ticket WK23).
 */
export function groupMunicipalityNamesByLetter(
  input: readonly (string | DirectoryMunicipality)[],
): LetterGroup[] {
  const collator = new Intl.Collator("de-DE", { sensitivity: "base" });
  const entries = input.map((raw) => (typeof raw === "string" ? { name: raw } : raw));
  const counts = new Map<string, number>();
  for (const entry of entries) {
    const key = municipalityDisplayName(entry.name).toLocaleLowerCase("de-DE");
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  const groups = new Map<string, { sortName: string; label: string }[]>();
  for (const entry of entries) {
    const name = municipalityDisplayName(entry.name);
    if (!name) continue;
    const labelled = entry.office !== undefined;
    const duplicate = (counts.get(name.toLocaleLowerCase("de-DE")) ?? 0) > 1;
    const label = labelled ? directoryLabel(entry, duplicate) : name;
    const first = name[0]!.toLocaleUpperCase("de-DE");
    const letter = LETTER_MAP[first] ?? (/^\p{L}$/u.test(first) ? first : "#");
    const bucket = groups.get(letter);
    const item = { sortName: name, label };
    if (bucket) bucket.push(item);
    else groups.set(letter, [item]);
  }
  return Array.from(groups.entries())
    .map(([letter, list]) => ({
      letter,
      names: [...list]
        .sort((a, b) => collator.compare(a.sortName, b.sortName) || collator.compare(a.label, b.label))
        .map((item) => item.label),
    }))
    .sort((a, b) => {
      if (a.letter === "#") return 1;
      if (b.letter === "#") return -1;
      return collator.compare(a.letter, b.letter);
    });
}

/**
 * Anzeigename einer Gemeinde: amtliche Zusätze nach dem ersten Komma
 * („Ladenburg, Stadt" -> „Ladenburg") entfallen, damit im Verzeichnis der
 * reine Ortsname als lesbarer Seitentext steht (Ticket WK20). Die gepflegten
 * Daten bleiben unverändert – nur die Anzeige wird bereinigt.
 */
export function municipalityDisplayName(name: string): string {
  const trimmed = name.trim();
  const comma = trimmed.indexOf(",");
  if (comma === -1) return trimmed;
  const head = trimmed.slice(0, comma).trim();
  return head || trimmed;
}

const LETTER_MAP: Record<string, string> = {
  Ä: "A",
  Ö: "O",
  Ü: "U",
  ß: "S",
};

/** Gesamtzahl der Namen im Verzeichnis – Grundlage der Zahl im Text. */
export function countNamesInGroups(groups: readonly LetterGroup[]): number {
  return groups.reduce((sum, group) => sum + group.names.length, 0);
}

export function formatMunicipalityCount(count: number): string {
  return new Intl.NumberFormat("de-DE").format(count);
}

export function directoryIntroduction(text: string, count: number): string {
  return text.replace(/\bX\b/u, formatMunicipalityCount(count));
}

/* ------------------------------------------------------- Kopfangaben */

export type LandesseiteContent = {
  bundesland: Bundesland;
  heading: string;
  seoTitle: string;
  seoDescription: string;
  introParagraphs: string[];
  sections: Array<{ heading: string; paragraphs: string[] }>;
  directoryHeading: string;
  directoryIntro: string;
  shareImageUrl: string | null;
};

/**
 * Freigabe 28.09.2026: Knopftext, Knopfziel und Einleitungszeile des
 * Verzeichnisses sind nicht mehr pflegbar. Das Formular ist fest eingebunden;
 * Überschrift und Rückfallziel sind feste Werte (bisherige Einträge).
 */
export const WAHLKALENDER_ANMELDUNG_LABEL = "Kostenlos anmelden";
export const WAHLKALENDER_FREEBIE_PATH = "/kostenlos/wahlkalender";

export type LandesseiteHeadEntry = { title: string } | ShareMetaEntry;

/**
 * Titel, Beschreibung und Vorschaubild kommen aus der gepflegten Zeile, das
 * Bild über dieselbe gemeinsame Stelle wie bei den Freebie-Landingpages
 * (Ticket LM11). Ohne Bild bleibt es bei der kleinen Kartenform.
 */
export function landesseiteHeadMeta(content: LandesseiteContent, url: string): LandesseiteHeadEntry[] {
  return [
    { title: content.seoTitle },
    { name: "description", content: content.seoDescription },
    { property: "og:title", content: content.seoTitle },
    { property: "og:description", content: content.seoDescription },
    { property: "og:type", content: "website" },
    { property: "og:url", content: url },
    ...shareImageMeta(content.shareImageUrl),
  ];
}
